import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { SearchOutlined } from "@mui/icons-material";
import { Box, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { ApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { NavigateIcon, SystemIcon } from "components/Icons/Icons";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { enqueueSnackbar } from "notistack";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";

export default function AipPassVendorListWorkspace() {
  const { globalState } = useContext(AppContext);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { addAttachments } = useChatAssistantAttachments();

  const loadVendors = useCallback(async () => {
    setIsLoading(true);
    try {
      const vendors = await ApiHelper.callPost("aip-pass/vendor/search", {}, globalState);
      setRows(vendors ?? []);
    } catch (err) {
      enqueueSnackbar(err?.message ?? "Failed to load AIP PASS vendors.", { variant: "error", preventDuplicate: true });
    } finally {
      setIsLoading(false);
    }
  }, [globalState]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const lower = searchTerm.toLowerCase();
    return rows.filter((r) => (r ?? "").toLowerCase().includes(lower));
  }, [rows, searchTerm]);

  const vendorRows = useMemo(
    () =>
      filteredRows.map((vendor) => ({
        id: vendor,
        vendor,
      })),
    [filteredRows]
  );

  useEffect(() => {
    addAttachments([{ name: "AIP PASS Vendors", content: filteredRows }]);
  }, [filteredRows, addAttachments]);

  const openVendor = useCallback(
    (vendor) => {
      navigate(`/system/aip-pass/${encodeURIComponent(vendor)}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: "vendor",
        headerName: "Server / Vendor",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerAlign: "right",
        align: "right",
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key="navigate"
            icon={<NavigateIcon />}
            label="View Details"
            onClick={() => openVendor(params.row.vendor)}
          />,
        ],
      },
    ],
    [openVendor]
  );

  return (
    <>
      <WorkspaceHeader
        title="AIP PASS"
        category={{
          title: "System",
          icon: <SystemIcon />,
        }}
      />

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <SearchOutlined />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ height: 600, width: "100%" }}>
        <TilleyDataGrid
          rows={vendorRows}
          columns={columns}
          pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
          defaultPageSize={10}
          getRowId={(r) => r.id}
          isLoading={isLoading}
          onRowClick={(params) => openVendor(params.row.vendor)}
        />
      </Box>
    </>
  );
}
